import type { FC, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import type { Settings } from '@/types/content'

import { useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BrandMark } from '@/components/generals/brand-mark'
import { CtaButton } from '@/components/generals/cta-button'
import { cms, hasValue, padNumber, toTelHref } from '@/lib/cms'

import { mainNavigation } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
    settings: Settings
}

const isActive = (pathname: string, href: string): boolean =>
    href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)

// Full screen navigation for small and medium screens. Traps focus, closes on Escape and on navigation.
export const MobileMenu: FC<MobileMenuProps> = ({ open, onClose, settings }): ReactNode => {
    const router = useRouter()
    const panelRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        const onRouteChange = () => onClose()

        router.events.on('routeChangeStart', onRouteChange)

        return () => {
            document.body.style.overflow = previousOverflow
            router.events.off('routeChangeStart', onRouteChange)
        }
    }, [open, onClose, router.events])

    const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault()
            onClose()

            return
        }

        if (event.key !== 'Tab' || !panelRef.current) return

        const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (!first || !last) return

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        }

        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    if (!open) return null

    return (
        <div
            id='mobile-menu'
            ref={panelRef}
            role='dialog'
            aria-modal='true'
            aria-label='Menü'
            onKeyDown={onKeyDown}
            className='fixed inset-0 z-50 flex flex-col overflow-y-auto bg-ink text-bone animate-in fade-in duration-300 xl:hidden'
        >
            <div aria-hidden='true' className='grain-layer' />

            <div className='relative mx-auto flex w-full max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12 h-[var(--header-height)] shrink-0'>
                <BrandMark settings={settings} tone='dark' onClick={onClose} />

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    aria-label='Menü schließen'
                    className='flex size-12 items-center justify-center rounded-full border border-bone/25 transition-colors duration-300 hover:bg-bone/10'
                >
                    <X className='size-5' strokeWidth={1.75} />
                </button>
            </div>

            <nav aria-label='Hauptnavigation' className='relative mx-auto w-full max-w-[1440px] flex-1 px-5 pt-6 sm:px-8 lg:px-12'>
                <ul className='border-t border-ink-line'>
                    {mainNavigation.map((item, index) => {
                        const active = isActive(router.pathname, item.href)

                        return (
                            <li
                                key={item.href}
                                className='border-b border-ink-line animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-500 ease-brand'
                                style={{ animationDelay: `${80 + index * 50}ms` }}
                            >
                                <Link
                                    href={item.href}
                                    onClick={onClose}
                                    aria-current={active ? 'page' : undefined}
                                    className='group flex items-baseline gap-4 py-4 sm:py-5'
                                >
                                    <span className={cn('type-label w-6 shrink-0', active ? 'text-ember-bright' : 'text-ash')}>
                                        {padNumber(index)}
                                    </span>

                                    <span className={cn('type-display text-[2.4rem] leading-none transition-colors duration-300 sm:text-[3rem]', active ? 'text-bone' : 'text-bone/75 group-hover:text-bone')}>
                                        {item.label}
                                    </span>
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </nav>

            <div className='relative mx-auto flex w-full max-w-[1440px] flex-col gap-6 px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-10 sm:px-8 lg:px-12'>
                <CtaButton settings={settings} tone='dark' size='lg' className='w-full sm:w-auto sm:self-start' onClick={onClose} />

                {(hasValue(settings.email) || hasValue(settings.telefon)) && (
                    <div className='flex flex-col gap-2 text-sm text-ash'>
                        {hasValue(settings.email) && (
                            <a href={`mailto:${settings.email}`} className='link-underline self-start'>
                                <span data-mdlfy-cms={cms('einstellungen', settings.id, 'email')}>{settings.email}</span>
                            </a>
                        )}

                        {hasValue(settings.telefon) && (
                            <a href={toTelHref(settings.telefon)} className='link-underline self-start'>
                                <span data-mdlfy-cms={cms('einstellungen', settings.id, 'telefon')}>{settings.telefon}</span>
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
